import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import NotificationBell from "./components/NotificationBell";
import GlobalSearch from "./components/GlobalSearch";
import LoginPage from "./pages/LoginPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import SplashScreen from "./components/SplashScreen";
import RegisterPage from "./pages/RegisterPage";
import ProfilePage from "./pages/ProfilePage";
import DashboardPage from "./pages/DashboardPage";
import EmployeesPage from "./pages/EmployeesPage";
import TimeEntriesPage from "./pages/TimeEntriesPage";
import ProjectsPage from "./pages/ProjectsPage";
import ProjectDetailPage from "./pages/ProjectDetailPage";
import FinancialsPage from "./pages/FinancialsPage";
import PartnersPage from "./pages/PartnersPage";
import InvoicesPage from "./pages/InvoicesPage";
import ProposalsPage from "./pages/ProposalsPage";
import EstimatesPage from "./pages/EstimatesPage";
import OpsManagerPage from "./pages/OpsManagerPage";
import CompaniesPage from "./pages/CompaniesPage";
import ContactsPage from "./pages/ContactsPage";
import LeadsPage from "./pages/LeadsPage";
import LeadsPipelineNew from "./pages/LeadsPipelineNew";
import LeadDetailPage from "./pages/LeadDetailPage";
import TasksPage from "./pages/TasksPage";
import EmailTemplatesPage from "./pages/EmailTemplatesPage";
import AnalyticsPage from "./pages/AnalyticsPage";
import SettingsPage from "./pages/SettingsPage";
import { 
  LayoutDashboard, 
  Users, 
  Clock, 
  Briefcase, 
  DollarSign, 
  UserCog, 
  FileText, 
  Building2, 
  Contact, 
  Target, 
  Calendar,
  Calculator,
  ChevronLeft,
  ChevronRight,
  Bell,
  Settings,
  Menu,
  X,
  LogOut,
  User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
export const api = axios.create({
  baseURL: API,
});

// Add request interceptor to include auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [crewTrackerExpanded, setCrewTrackerExpanded] = useState(false);
  const [crmExpanded, setCrmExpanded] = useState(false);
  
  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Auto-expand sections based on current route
  useEffect(() => {
    const path = location.pathname;
    const crewTrackerPaths = ['/employees', '/time-entries', '/projects', '/financials', '/partners'];
    const crmPaths = ['/leads', '/estimates', '/proposals', '/companies', '/contacts', '/tasks', '/invoices'];
    
    if (crewTrackerPaths.some(p => path.startsWith(p))) {
      setCrewTrackerExpanded(true);
    }
    if (crmPaths.some(p => path.startsWith(p))) {
      setCrmExpanded(true);
    }
  }, [location.pathname]);

  const crewTrackerNavItems = [
    { path: "/employees", label: "Employees", icon: Users },
    { path: "/time-entries", label: "Time Entries", icon: Clock },
    { path: "/projects", label: "Projects", icon: Briefcase },
    { path: "/financials", label: "Financials", icon: DollarSign },
    { path: "/partners", label: "Partners", icon: UserCog },
  ];

  const crmNavItems = [
    { path: "/leads", label: "Leads", icon: Target },
    { path: "/estimates", label: "Estimates", icon: Calculator },
    { path: "/proposals", label: "Proposals", icon: FileText },
    { path: "/invoices", label: "Invoices", icon: FileText },
    { path: "/tasks", label: "Calendar & Tasks", icon: Calendar },
    { path: "/companies", label: "Companies", icon: Building2 },
    { path: "/contacts", label: "Contacts", icon: Contact },
  ];

  const NavItem = ({ item, collapsed }) => {
    const Icon = item.icon;
    const active = isActive(item.path);
    
    return (
      <Link
        to={item.path}
        className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
          active
            ? "bg-blue-600 text-white shadow-md"
            : "text-slate-700 hover:bg-slate-100"
        }`}
        title={collapsed ? item.label : ""}
      >
        <Icon className={`h-5 w-5 ${active ? "text-white" : "text-slate-600"}`} />
        {!collapsed && <span className="font-medium text-sm">{item.label}</span>}
      </Link>
    );
  };

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Sidebar - Desktop */}
      <aside
        className={`hidden md:flex flex-col bg-white border-r border-slate-200 transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-20"
        }`}
      >
        {/* Logo Section */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_clean-crew-tracker/artifacts/tzw4ofm2_Final_cleanifax_logo_small.png"
                  alt="Cleanifax"
                  className="h-10 w-auto"
                />
                <div>
                  <h2 className="text-lg font-bold text-slate-900">Cleanifax</h2>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 hover:bg-slate-100 rounded"
              >
                <ChevronLeft className="h-5 w-5 text-slate-600" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1 hover:bg-slate-100 rounded mx-auto"
            >
              <ChevronRight className="h-5 w-5 text-slate-600" />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-2">
          {/* Dashboard - Standalone */}
          <div className="mb-4">
            <Link
              to="/"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                isActive("/") || isActive("/dashboard")
                  ? "bg-blue-600 text-white shadow-md"
                  : "text-slate-700 hover:bg-slate-100"
              }`}
              title={!sidebarOpen ? "Dashboard" : ""}
            >
              <LayoutDashboard className={`h-5 w-5 ${isActive("/") || isActive("/dashboard") ? "text-white" : "text-slate-600"}`} />
              {sidebarOpen && <span className="font-medium text-sm">Dashboard</span>}
            </Link>
          </div>

          {/* Ops Manager - Standalone */}
          <div className="mb-4">
            <Link
              to="/ops-manager"
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                isActive("/ops-manager")
                  ? "bg-gradient-to-r from-[#247C75] to-[#1d6259] text-white shadow-md"
                  : "text-slate-700 hover:bg-slate-100"
              }`}
              title={!sidebarOpen ? "Ops Manager" : ""}
            >
              <Briefcase className={`h-5 w-5 ${isActive("/ops-manager") ? "text-white" : "text-slate-600"}`} />
              {sidebarOpen && <span className="font-medium text-sm">Ops Manager</span>}
            </Link>
          </div>

          {/* Project Tracker Section - Collapsible */}
          <div>
            <button
              onClick={() => setCrewTrackerExpanded(!crewTrackerExpanded)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-100 rounded-lg transition-all"
            >
              {sidebarOpen ? (
                <>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Project Tracker
                  </h3>
                  <ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${crewTrackerExpanded ? 'rotate-90' : ''}`} />
                </>
              ) : (
                <div className="h-px bg-slate-200 w-full" />
              )}
            </button>
            {crewTrackerExpanded && (
              <div className="space-y-1 mt-2 ml-2">
                {crewTrackerNavItems.map((item) => (
                  <NavItem key={item.path} item={item} collapsed={!sidebarOpen} />
                ))}
              </div>
            )}
          </div>

          {/* CRM Section - Collapsible */}
          <div>
            <button
              onClick={() => setCrmExpanded(!crmExpanded)}
              className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-100 rounded-lg transition-all"
            >
              {sidebarOpen ? (
                <>
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    CRM
                  </h3>
                  <ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${crmExpanded ? 'rotate-90' : ''}`} />
                </>
              ) : (
                <div className="h-px bg-slate-200 w-full" />
              )}
            </button>
            {crmExpanded && (
              <div className="space-y-1 mt-2 ml-2">
                {crmNavItems.map((item) => (
                  <NavItem key={item.path} item={item} collapsed={!sidebarOpen} />
                ))}
              </div>
            )}
          </div>
        </nav>

        {/* Settings at bottom */}
        <div className="p-4 border-t border-slate-200">
          <Link
            to="/settings"
            className="flex items-center gap-3 px-3 py-2.5 text-slate-700 hover:bg-slate-100 rounded-lg transition-all"
            title={!sidebarOpen ? "Settings" : ""}
          >
            <Settings className="h-5 w-5 text-slate-600" />
            {sidebarOpen && <span className="font-medium text-sm">Settings</span>}
          </Link>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-50 bg-black bg-opacity-50" onClick={() => setMobileMenuOpen(false)}>
          <aside
            className="absolute left-0 top-0 bottom-0 w-64 bg-white"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200">
              <div className="flex items-center gap-2">
                <img
                  src="https://customer-assets.emergentagent.com/job_clean-crew-tracker/artifacts/tzw4ofm2_Final_cleanifax_logo_small.png"
                  alt="Cleanifax"
                  className="h-10 w-auto"
                />
                <h2 className="text-lg font-bold text-slate-900">Cleanifax</h2>
              </div>
              <button onClick={() => setMobileMenuOpen(false)}>
                <X className="h-6 w-6 text-slate-600" />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto p-4 space-y-2">
              {/* Dashboard - Standalone */}
              <div className="mb-4">
                <Link
                  to="/"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive("/") || isActive("/dashboard")
                      ? "bg-blue-600 text-white shadow-md"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <LayoutDashboard className={`h-5 w-5 ${isActive("/") || isActive("/dashboard") ? "text-white" : "text-slate-600"}`} />
                  <span className="font-medium text-sm">Dashboard</span>
                </Link>
              </div>

              {/* Ops Manager - Standalone */}
              <div className="mb-4">
                <Link
                  to="/ops-manager"
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all ${
                    isActive("/ops-manager")
                      ? "bg-gradient-to-r from-[#247C75] to-[#1d6259] text-white shadow-md"
                      : "text-slate-700 hover:bg-slate-100"
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Briefcase className={`h-5 w-5 ${isActive("/ops-manager") ? "text-white" : "text-slate-600"}`} />
                  <span className="font-medium text-sm">Ops Manager</span>
                </Link>
              </div>

              {/* Project Tracker Section - Collapsible */}
              <div>
                <button
                  onClick={() => setCrewTrackerExpanded(!crewTrackerExpanded)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-100 rounded-lg transition-all"
                >
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Project Tracker
                  </h3>
                  <ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${crewTrackerExpanded ? 'rotate-90' : ''}`} />
                </button>
                {crewTrackerExpanded && (
                  <div className="space-y-1 mt-2 ml-2">
                    {crewTrackerNavItems.map((item) => (
                      <NavItem key={item.path} item={item} collapsed={false} />
                    ))}
                  </div>
                )}
              </div>

              {/* CRM Section - Collapsible */}
              <div>
                <button
                  onClick={() => setCrmExpanded(!crmExpanded)}
                  className="w-full flex items-center justify-between px-3 py-2 hover:bg-slate-100 rounded-lg transition-all"
                >
                  <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    CRM
                  </h3>
                  <ChevronRight className={`h-4 w-4 text-slate-500 transition-transform ${crmExpanded ? 'rotate-90' : ''}`} />
                </button>
                {crmExpanded && (
                  <div className="space-y-1 mt-2 ml-2">
                    {crmNavItems.map((item) => (
                      <NavItem key={item.path} item={item} collapsed={false} />
                    ))}
                  </div>
                )}
              </div>
            </nav>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Bar */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-6">
          {/* Left Side - Mobile Menu + Logo */}
          <div className="flex items-center gap-3">
            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2 hover:bg-slate-100 rounded-lg"
              onClick={() => setMobileMenuOpen(true)}
            >
              <Menu className="h-6 w-6 text-slate-600" />
            </button>
            
            {/* Mobile Logo */}
            <img
              src="https://customer-assets.emergentagent.com/job_clean-crew-tracker/artifacts/tzw4ofm2_Final_cleanifax_logo_small.png"
              alt="Cleanifax"
              className="h-8 w-auto md:hidden"
            />
          </div>

          {/* Center - Global Search */}
          <div className="flex-1 max-w-2xl mx-4 hidden lg:block">
            <GlobalSearch />
          </div>

          {/* Right Side - Notifications & User */}
          <div className="flex items-center gap-2 md:gap-4">
            {/* Mobile Search */}
            <div className="lg:hidden">
              <GlobalSearch />
            </div>
            <NotificationBell />
            <div className="relative pl-2 md:pl-4 md:border-l border-slate-200 user-menu-container">
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 md:gap-3 hover:bg-slate-50 rounded-lg p-1 md:p-2 transition-colors"
              >
                <div className="text-right hidden md:block">
                  <p className="text-sm font-medium text-slate-900">
                    {user?.first_name} {user?.last_name}
                  </p>
                  <p className="text-xs text-slate-500">{user?.email}</p>
                </div>
                <div className="h-9 w-9 md:h-10 md:w-10 rounded-full bg-teal-600 text-white flex items-center justify-center font-semibold text-sm md:text-base">
                  {user?.first_name?.[0]}{user?.last_name?.[0]}
                </div>
              </button>
              
              {/* User Dropdown Menu */}
              {userMenuOpen && (
                <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-slate-200 py-2 z-50">
                  <div className="px-4 py-3 border-b border-slate-200">
                    <p className="text-sm font-medium text-slate-900">
                      {user?.first_name} {user?.last_name}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">{user?.email}</p>
                    <span className="inline-block mt-2 px-2 py-1 bg-teal-100 text-teal-800 rounded text-xs font-medium">
                      {user?.role}
                    </span>
                  </div>
                  <Link
                    to="/profile"
                    onClick={() => setUserMenuOpen(false)}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors"
                  >
                    <User className="h-4 w-4 text-slate-600" />
                    <span className="text-sm text-slate-700">Profile Settings</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors w-full text-left"
                  >
                    <LogOut className="h-4 w-4 text-slate-600" />
                    <span className="text-sm text-slate-700">Logout</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <div className="container mx-auto p-4 md:p-6 max-w-7xl">
            {children}
          </div>
        </main>
      </div>

      <Toaster position="top-right" />
    </div>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);

  // Show splash screen only on initial load
  useEffect(() => {
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    if (hasSeenSplash) {
      setShowSplash(false);
    }
  }, []);

  const handleSplashFinish = () => {
    sessionStorage.setItem('hasSeenSplash', 'true');
    setShowSplash(false);
  };

  if (showSplash) {
    return <SplashScreen onFinish={handleSplashFinish} />;
  }

  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/register" element={<RegisterPage />} />
          
          {/* Protected Routes */}
          <Route path="/*" element={
            <ProtectedRoute>
              <Layout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/profile" element={<ProfilePage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/employees" element={<EmployeesPage />} />
                  <Route path="/time-entries" element={<TimeEntriesPage />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
                  <Route path="/financials" element={<FinancialsPage />} />
                  <Route path="/partners" element={<PartnersPage />} />
                  <Route path="/invoices" element={<InvoicesPage />} />
                  <Route path="/estimates" element={<EstimatesPage />} />
                  <Route path="/estimates/new" element={<EstimatesPage />} />
                  <Route path="/proposals" element={<ProposalsPage />} />
                  <Route path="/ops-manager" element={<OpsManagerPage />} />
                  <Route path="/companies" element={<CompaniesPage />} />
                  <Route path="/contacts" element={<ContactsPage />} />
                  <Route path="/leads" element={<LeadsPipelineNew />} />
                  <Route path="/leads/old" element={<LeadsPage />} />
                  <Route path="/leads/:leadId" element={<LeadDetailPage />} />
                  <Route path="/tasks" element={<TasksPage />} />
                  <Route path="/email-templates" element={<EmailTemplatesPage />} />
                  <Route path="/analytics" element={<AnalyticsPage />} />
                </Routes>
              </Layout>
            </ProtectedRoute>
          } />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
